import React, { useEffect, useState } from 'react';
import {
  tenantMeService,
  TenantMeRead,
  TenantMeUser,
  TenantMeDomainConfig,
} from '../services/tenantMeService';
import { PageContainer } from '../components/layout';

export const MonTenantPage: React.FC = () => {
  const [me, setMe] = useState<TenantMeRead | null>(null);
  const [users, setUsers] = useState<TenantMeUser[]>([]);
  const [domains, setDomains] = useState<TenantMeDomainConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      tenantMeService.getMe(),
      tenantMeService.getMyUsers(),
      tenantMeService.getMyDomains(),
    ])
      .then(([m, u, d]) => {
        if (cancelled) return;
        setMe(m);
        setUsers(u);
        setDomains(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Erreur lors du chargement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <div>Chargement…</div>
      </PageContainer>
    );
  }

  if (error || !me) {
    return (
      <PageContainer>
        <div className="text-red-600">{error || 'Tenant introuvable'}</div>
      </PageContainer>
    );
  }

  const formatPolicy = (p: string) => {
    if (p === 'auto') return 'Automatique';
    if (p === 'manual') return 'Manuelle';
    if (p === 'scheduled') return 'Planifiée';
    return p;
  };

  return (
    <PageContainer>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Mon tenant</h1>
        <p className="text-sm text-gray-500">Configuration courante (lecture seule)</p>
      </div>

      <section className="bg-white rounded shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Identité</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <dt className="text-gray-500">Nom</dt>
          <dd>{me.name}</dd>
          <dt className="text-gray-500">Slug</dt>
          <dd className="font-mono">{me.slug}</dd>
          <dt className="text-gray-500">Statut</dt>
          <dd>
            {me.is_active ? (
              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">Actif</span>
            ) : (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs">Inactif</span>
            )}
          </dd>
          <dt className="text-gray-500">Authentification</dt>
          <dd>{me.auth_mode}</dd>
        </dl>
      </section>

      <section className="bg-white rounded shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Domaines actifs</h2>
        {domains.length === 0 ? (
          <div className="text-sm text-gray-500">Aucun domaine activé.</div>
        ) : (
          <div className="space-y-4">
            {domains.map((d) => (
              <div key={d.domain_code} className="border rounded p-3">
                <div className="font-medium mb-2">{d.domain_code}</div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <dt className="text-gray-500">Schéma</dt>
                  <dd>{d.schema_name || <span className="text-gray-400">— non assigné</span>}</dd>
                  <dt className="text-gray-500">Prompt</dt>
                  <dd>{d.prompt_name || <span className="text-gray-400">— non assigné</span>}</dd>
                  <dt className="text-gray-500">Config OCR</dt>
                  <dd>{d.ocr_config_name || <span className="text-gray-400">— non assigné</span>}</dd>
                  <dt className="text-gray-500">Config LLM</dt>
                  <dd>{d.llm_config_name || <span className="text-gray-400">— non assigné</span>}</dd>
                </dl>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white rounded shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Utilisateurs du tenant</h2>
        {users.length === 0 ? (
          <div className="text-sm text-gray-500">Aucun utilisateur.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">Email</th>
                <th className="py-2">Nom complet</th>
                <th className="py-2">Rôle</th>
                <th className="py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-gray-100">
                  <td className="py-2">{u.email}</td>
                  <td className="py-2">{u.full_name || '-'}</td>
                  <td className="py-2">{u.role}</td>
                  <td className="py-2">
                    {u.is_active ? (
                      <span className="text-green-700">Actif</span>
                    ) : (
                      <span className="text-gray-400">Inactif</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-white rounded shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Storage</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <dt className="text-gray-500">Backend</dt>
          <dd>{me.storage.backend}</dd>
          <dt className="text-gray-500">Bucket</dt>
          <dd className="font-mono">{me.storage.bucket || '-'}</dd>
          <dt className="text-gray-500">Préfixe</dt>
          <dd className="font-mono">{me.storage.key_prefix || '-'}</dd>
          <dt className="text-gray-500">Région</dt>
          <dd>{me.storage.region || '-'}</dd>
        </dl>
        <p className="text-xs text-gray-400 mt-3">
          Les credentials d'accès au stockage ne sont jamais affichés ici.
        </p>
      </section>

      <section className="bg-white rounded shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Politique d'extraction</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <dt className="text-gray-500">Mode</dt>
          <dd>{formatPolicy(me.extraction_policy)}</dd>
          {me.extraction_policy === 'scheduled' && (
            <>
              <dt className="text-gray-500">Cron</dt>
              <dd className="font-mono">{me.extraction_schedule_cron || '-'}</dd>
            </>
          )}
        </dl>
      </section>

      <div className="text-sm text-gray-500 italic">
        💡 Pour toute modification, contactez votre administrateur CMSD.
      </div>
    </PageContainer>
  );
};

export default MonTenantPage;
